import { updateDataSettings } from '@wix/ambassador-data-v1-data-settings/http';
import { translate } from '#packages/i18n';
import { ErrorReporter } from '@wix/editor-error-reporter';
import { WIX_CMS, DATA_BINDING, WIX_SERVICES } from '@wix/app-definition-ids';
import Constants from '#packages/constants';
import type { EditorAPI } from '@/editorAPI';
import { HttpClient } from '@wix/http-client';
import type { CompRef } from 'types/documentServices';
import type { DatabindingPublicApi } from '@wix/wix-data-client-types';
import { FieldType } from '@wix/wix-data-schema-types';
import constants from '@/constants';
import { ServiceSectionIds } from '../types';
import { countServices } from '@wix/ambassador-bookings-services-v2-service/http';
import { injectLeadNurturingAppData } from '@wix/ambassador-genie-inject-lead-nurturing-v1-no-entity/http';
import { isDataModeOn } from '#packages/wixData';
import experiment from 'experiment';

const installCMS = async (sectionRef: CompRef, editorAPI: EditorAPI) => {
  editorAPI.selection.deselectComponents();

  if (isDataModeOn(editorAPI)) {
    return;
  }

  try {
    if (experiment.isOpen('specs.wixDataClient.provisionCmsApp')) {
      await editorAPI.tpa.installAppIfNeeded(WIX_CMS);
    } else {
      await new Promise((resolve, reject) => {
        editorAPI.wixCode.provision({
          onSuccess: resolve,
          onError: reject,
        });
      });
    }

    const { applicationId } =
      editorAPI.platform.getAppDataByAppDefId(DATA_BINDING);

    editorAPI.platform.notifyApplication(applicationId, {
      eventType: 'enableDataModeClicked',
      eventPayload: { origin: 'WixServicesSection' },
    });
  } catch (error) {
    ErrorReporter.captureException(error, {
      tags: { failedToInstallCMS: true },
    });
  }
};

const connectRepeaterToCMS = async (
  sectionRef: CompRef,
  editorAPI: EditorAPI,
  serviceId: ServiceSectionIds,
) => {
  const [repeaterRef] = editorAPI.components.get.byType(
    Constants.COMP_TYPES.REPEATER,
    sectionRef,
  );

  if (!repeaterRef) {
    return;
  }
  const pageRef = editorAPI.pages.getCurrentPage();

  const databindingPublicApi = (await editorAPI.dsRead.platform.getAppPublicApi(
    DATA_BINDING,
  )) as unknown as DatabindingPublicApi;

  const { controllerRef: datasetRef } =
    await databindingPublicApi.createDataset(pageRef, {
      displayName: translate('Dataset_DisplayName_InquiryService'),
      collectionId: 'Services/InquiryService',
    });

  // repeater connection
  await databindingPublicApi.connectBindingSection({
    componentRef: repeaterRef,
    controllerRef: datasetRef,
    newBindings: [],
    role: 'repeaterRole',
    requiresPrimaryConnection: true,
  });

  switch (serviceId) {
    case ServiceSectionIds.SERVICE_1: {
      await connectService1ToCMS(
        repeaterRef,
        datasetRef,
        editorAPI,
        databindingPublicApi,
      );
      break;
    }
    case ServiceSectionIds.SERVICE_2: {
      await connectService2ToCMS(
        repeaterRef,
        datasetRef,
        editorAPI,
        databindingPublicApi,
      );
      break;
    }
  }
};

const connectService1ToCMS = async (
  repeaterRef: CompRef,
  datasetRef: CompRef,
  editorAPI: EditorAPI,
  databindingPublicApi: DatabindingPublicApi,
) => {
  // text connection
  const [textRef] = editorAPI.components.get.byType(
    Constants.COMP_TYPES.TEXT,
    repeaterRef,
  );

  await databindingPublicApi.connectBindingSection({
    componentRef: textRef,
    controllerRef: datasetRef,
    newBindings: [
      {
        type: 'Field',
        fieldPath: 'name',
        fieldType: FieldType.text,
        prop: '$text',
      },
    ],
    role: 'textRole',
    requiresPrimaryConnection: true,
  });

  // image connection
  const [imageRef] = editorAPI.components.get.byType(
    Constants.COMP_TYPES.PHOTO,
    repeaterRef,
  );

  await databindingPublicApi.connectBindingSection({
    componentRef: imageRef,
    controllerRef: datasetRef,
    newBindings: [
      {
        type: 'Field',
        fieldPath: 'image',
        fieldType: FieldType.image,
        prop: 'src',
      },
      {
        type: 'Field',
        fieldPath: 'altText',
        fieldType: FieldType.text,
        prop: 'alt',
      },
    ],
    role: 'imageRole',
    requiresPrimaryConnection: true,
  });
};

const connectService2ToCMS = async (
  repeaterRef: CompRef,
  datasetRef: CompRef,
  editorAPI: EditorAPI,
  databindingPublicApi: DatabindingPublicApi,
) => {
  // text connection
  const [descriptionRef, textRef] = editorAPI.components.get.byType(
    Constants.COMP_TYPES.TEXT,
    repeaterRef,
  );

  // service name connection
  await databindingPublicApi.connectBindingSection({
    componentRef: textRef,
    controllerRef: datasetRef,
    newBindings: [
      {
        type: 'Field',
        fieldPath: 'name',
        fieldType: FieldType.text,
        prop: '$text',
      },
    ],
    role: 'textRole',
    requiresPrimaryConnection: true,
  });

  // description name connection
  await databindingPublicApi.connectBindingSection({
    componentRef: descriptionRef,
    controllerRef: datasetRef,
    newBindings: [
      {
        type: 'Field',
        fieldPath: 'description',
        fieldType: FieldType.text,
        prop: '$text',
      },
    ],
    role: 'textRole',
    requiresPrimaryConnection: true,
  });

  // image connection
  const [imageRef] = editorAPI.components.get.byType(
    Constants.COMP_TYPES.PHOTO,
    repeaterRef,
  );

  await databindingPublicApi.connectBindingSection({
    componentRef: imageRef,
    controllerRef: datasetRef,
    newBindings: [
      {
        type: 'Field',
        fieldPath: 'image',
        fieldType: FieldType.image,
        prop: 'src',
      },
      {
        type: 'Field',
        fieldPath: 'altText',
        fieldType: FieldType.text,
        prop: 'alt',
      },
    ],
    role: 'imageRole',
    requiresPrimaryConnection: true,
  });
};

export const enableCmsForWixApps = async (editorAPI: EditorAPI) => {
  const httpClient = new HttpClient({
    getAppToken: () =>
      editorAPI.dsRead.platform.getAppDataByApplicationId(
        constants.APPLICATIONS.META_SITE_APPLICATION_ID,
      )?.instance || '',
  });

  await httpClient.request(
    updateDataSettings({
      dataSettings: {
        appCollectionsVisible: true,
      },
    }),
  );
};

export const injectWixServicesContentIfNeeded = async (
  editorAPI: EditorAPI,
) => {
  const httpClient = new HttpClient({
    getAppToken: () =>
      editorAPI.dsRead.platform.getAppDataByApplicationId(
        constants.APPLICATIONS.META_SITE_APPLICATION_ID,
      )?.instance || '',
  });

  try {
    const { data: countResponse } = await httpClient.request(
      countServices({ filter: { appId: WIX_SERVICES } }),
    );

    if (countResponse.count === 0) {
      await httpClient.request(injectLeadNurturingAppData({}));
    }
  } catch (error) {
    console.error('Failed to count or inject services', error);
  }
};

export const wixServicesInstallAndConnectToCMS = async (
  sectionRef: CompRef,
  editorAPI: EditorAPI,
  serviceId: ServiceSectionIds,
): Promise<void> => {
  let abort = false;
  const openProgressBar = async () => {
    if (abort) return;
    if (editorAPI.panelHelpers.isProgressBarOpen()) {
      setTimeout(openProgressBar, 200);
    } else {
      await editorAPI.panelHelpers.openProgressBar(
        {
          title: translate('Bookings_ConnectCMS_Loader_Title'),
          totalSteps: 1,
          currentStep: 0,
          taskDisplayName: translate('Bookings_ConnectCMS_Loader_Text1'),
        },
        true,
      );
    }
  };

  const injectPromise = injectWixServicesContentIfNeeded(editorAPI);
  await installCMS(sectionRef, editorAPI);
  await enableCmsForWixApps(editorAPI);
  await editorAPI.waitForChangesAppliedAsync();
  try {
    openProgressBar();
  } catch (error) {
    console.warn('Failed to open progress bar', error);
  }
  await Promise.all([
    injectPromise,
    connectRepeaterToCMS(sectionRef, editorAPI, serviceId),
  ]);
  await editorAPI.waitForChangesAppliedAsync();

  // @ts-expect-error - refresh method exists but is not properly typed
  await editorAPI.dsActions.pages.refresh();
  await editorAPI.waitForChangesAppliedAsync();

  try {
    abort = true;
    await editorAPI.panelHelpers.updateProgressBar(
      1,
      translate('Bookings_ConnectCMS_Loader_Text2'),
    );
    await editorAPI.panelHelpers.closeProgressBar();
  } catch (error) {
    console.warn('Failed to update or close progress bar', error);
  }
};
